import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import ContentEditable from 'react-contenteditable'
import {createPortal} from 'react-dom'
import stringify from 'stringify-entities'
import SVGAddVariable from '../../../../../assets/icons/add-variable-small.svg'
import SVGChevronDown from '../../../../../assets/icons/chevron-down-thick.svg'
import {CardDefinition, ContextMapping} from '../../../../../common/types'
import {StyledDropdownContents} from '../../../../../components/library/dropdown/dropdown'
import {
  elementContainsSelection,
  getCurrentRange,
  placeCaretAtEnd
} from '../../../../../components/library/text-editor/selection-helpers'
import {
  AddVariableWrapper,
  EditorWrap,
  IconWrap,
  Label,
  RightIconWrapper,
  StyledTextEditor,
  TextEditorWrapper,
  Wrapper
} from '../../../../../components/library/text-editor/styles'
import {Tooltip} from '../../../../../components/library/tooltip/tooltip'
import {useEnvironment} from '../../../../../environment'
import useDropdownPosition from '../../../../../hooks/use-dropdown-position'
import {useActionFlowAnalytics} from '../../../../../lib/analytics/action-flow-analytics'
import ClickAwayListener from '../../../../../lib/click-away-listener'
import {pasteAsText} from '../../../../../lib/helpers'
import {OpenVariableForm} from '../../../../../providers/variables/use-variable-form'
import {useAFConfig, useAFDispatch} from '../../../state/Provider'
import {ButtonIcon} from '../button'
import {
  GetAttributes,
  getCardInput,
  makeNewPlaceholderElement,
  serialiseForEditor,
  variablesToHtml
} from './input-helpers'
import {ContextPicker, ContextSelection} from './picker'
import {PlaceholderModal} from './placeholder-modal'

type BaseProps = {
  value: string
  onChange: (value: string) => void
  contextMapping: ContextMapping
  label: ReactNode
  upLabel?: ReactNode
  multiline?: boolean
  onKeyPress?: (event: React.KeyboardEvent<HTMLDivElement>) => void
  dropdownEmptyMessage?: string
  variableButtonDisabled?: boolean
  readOnly?: boolean
  icon?: ReactNode
  openVariableForm: OpenVariableForm
  asDropdown?: boolean
  editableDropdown?: boolean
  pattern?: RegExp
  compact?: boolean
  className?: string
}

export type TemplateContextProps = BaseProps & {
  templateId: string
  stepId?: string
}

// This supports a more recent update that lets us use this component in steps outside of a card template
// In that case you must pass mode=step, this appeases TS and means we don't have to update historical usages
export type StepContextProps = BaseProps & {
  stepId: string
  mode: 'step'
}

export type InputContextProps = TemplateContextProps | StepContextProps

const variablesToHtmlForEditor = (
  value: string,
  getAttributes?: GetAttributes
) => {
  return variablesToHtml(
    stringify(String(value || ''), {escapeOnly: true}),
    getAttributes
  ).replace(/(?:\r\n|\r|\n)/g, '<br />')
}

/**
 * Returns a function that converts %{placeholders} within a string to html
 */
export const usePlaceholdersToHtml = (contextMapping: ContextMapping) => {
  const config = useAFConfig()
  // Given a string containing %{name_xyz}
  // this function is passed name_xyz and should return {type: 'context'|'profile', label: 'name'}
  // based on the data within contextMapping
  const getPlaceholderAttributes = useCallback(
    (mappingId: string): {label?: string; type: string} => {
      const mapping = contextMapping?.[mappingId]
      const type = mapping?.type ?? 'variable'

      if (!mapping) {
        return {type}
      }

      if (mapping.referenceId) {
        // We store the inputs key in the referenceId, use that to lookup the current name of the input
        for (const definition of Object.values(config.cardTemplates ?? {})) {
          const found = getCardInput(
            definition as CardDefinition,
            mapping.referenceId
          )

          if (found) {
            return {
              type,
              label:
                found && 'name' in found.attributes
                  ? (found.attributes.name as string)
                  : undefined
            }
          }
        }
      }
      return {
        type,
        label: mapping.display ?? mappingId
      }
    },
    [config.cardTemplates, contextMapping]
  )

  const placeholdersToHtml = useCallback(
    (value: string) =>
      variablesToHtmlForEditor(value, getPlaceholderAttributes),
    [getPlaceholderAttributes]
  )

  return placeholdersToHtml
}

export const InputContext = ({
  label,
  icon,
  upLabel,
  readOnly,
  onChange,
  stepId,
  openVariableForm,
  multiline,
  contextMapping,
  variableButtonDisabled,
  pattern,
  compact,
  ...props
}: InputContextProps) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const [focused, setFocused] = useState<boolean>(false)
  const [showContext, setShowContext] = useState<boolean>(false)
  const [editPlaceholder, setEditPlaceholder] = useState<string>()
  const dispatch = useAFDispatch()
  const lastRange = useRef<Range>()
  const viaKeyboard = useRef<boolean>(false)
  const {track} = useActionFlowAnalytics()
  const {environment} = useEnvironment()
  const placeholdersToHtml = usePlaceholdersToHtml(contextMapping)
  const asDropdown = props.asDropdown ?? props.editableDropdown ?? false

  const [localValue, setLocalValue] = React.useState<string>(() =>
    placeholdersToHtml(String(props.value ?? ''))
  )

  const handleFocus = useCallback(() => {
    if (!readOnly) {
      setFocused(true)
    }
  }, [readOnly])

  const closeDropdown = useCallback(() => {
    setShowContext(false)
  }, [])

  const handleChange = useCallback(() => {
    if (!readOnly) {
      const value = ref.current?.innerHTML ?? ''

      setLocalValue(value)
      const serialised = serialiseForEditor(value, multiline)
      onChange(serialised)
    }
  }, [readOnly, onChange, multiline])

  const handleKeyUp = useCallback<React.KeyboardEventHandler<HTMLDivElement>>(
    e => {
      if (asDropdown) {
        // we don't need to listen for + key if we're a dropdown
        return
      }
      if ('key' in e && e.key === '+') {
        const sel = window.getSelection()
        lastRange.current = sel?.getRangeAt(0)
        viaKeyboard.current = true

        setShowContext(true)
      }
    },
    [asDropdown]
  )

  const insertPlaceholder = useCallback(
    (attributes: {id: string; label?: string; type: string}) => {
      const el = ref.current

      if (!el) {
        return
      }

      // restore selection if we can
      if (lastRange.current) {
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(lastRange.current)
      }

      // if selection isn't within the right element, fall back to inserting at end
      if (!elementContainsSelection(el)) {
        placeCaretAtEnd(el)
      }

      const sel = window.getSelection()

      if (sel) {
        let range = sel.getRangeAt(0)
        if (viaKeyboard.current) {
          // go back and delete the + if that's how we opened the dropdown
          range.setStart(
            range.startContainer,
            Math.max(range.startOffset - 1, 0)
          )
          range.deleteContents()
        }

        // insert the variable
        const node = makeNewPlaceholderElement(attributes)
        range.insertNode(node)

        if (!asDropdown) {
          // Incantations to get the cursor after the variable with a space
          range = range.cloneRange()
          range.setStartAfter(node)
          const space = document.createTextNode('\u00A0') // non-breaking space &nbsp; doesn't work and a regular space is trimmed
          range.insertNode(space)
          range.setStartAfter(space)
        }

        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)

        handleChange()
        viaKeyboard.current = false
      } else {
        // this shouldn't happen, because of the insert-at-end fallback above
        console.warn('Could not insert new variable')
      }
    },
    [handleChange, asDropdown]
  )

  // Are we using this component in a card template or a step?
  const mode = useMemo<
    {mode: 'template'; templateId: string} | {mode: 'step'; stepId: string}
  >(
    () =>
      'templateId' in props
        ? {templateId: props.templateId, mode: 'template'}
        : {
            stepId: stepId as string, // we know that stepId must be defined if we don't have a template id due to prop type
            mode: 'step'
          },
    [props, stepId]
  )

  const addContextMapping = useCallback(
    (selection: ContextSelection) => {
      if (asDropdown) {
        // clear out existing value if acting as a dropdown
        if (ref.current?.innerHTML) {
          ref.current.innerHTML = ''
        }
        setLocalValue('')
      }
      if (selection.type !== 'variable') {
        dispatch({
          type: 'add-context-mapping',
          mappingId: selection.id,
          mapping: {
            display: selection.label,
            type: selection.type,
            referenceId: selection.referenceId,
            mapping: {type: 'path', path: selection.path}
          },
          ...(mode.mode === 'step'
            ? {stepId: mode.stepId}
            : {templateId: mode.templateId})
        })
      }
      setShowContext(false)

      insertPlaceholder({
        id: selection.id,
        label: selection.label,
        type: selection.type
      })

      track({
        event: 'Inserted action flow context placeholder',
        properties: {
          placeholder_type: selection.type,
          card_template_id:
            mode.mode === 'template' ? mode.templateId : undefined
        }
      })
    },
    [insertPlaceholder, track, mode, dispatch, asDropdown]
  )

  const [dropdownRef, dropdownPosition] = useDropdownPosition(
    ref,
    showContext,
    closeDropdown
  )

  useEffect(() => {
    // When not focused, act as a controlled component, but only update the
    // local rendered value if the incoming value has actually changed, to avoid
    // breaking selection ranges stored in this.lastRange.
    // Determine this by serialising the current local value and checking it
    // against the incoming value (which is serialised in handleChange)
    if (!focused) {
      setLocalValue(current => {
        const serialisedLocalValue = serialiseForEditor(current, multiline)

        if (serialisedLocalValue !== props.value) {
          return placeholdersToHtml(props.value)
        }
        return current
      })
    }
  }, [
    props.value,
    focused,
    multiline,
    // unfortunately this is going to cause a re-render, we should be saved by the
    // focused check and then returning the same value if nothing has changed
    placeholdersToHtml
  ])

  const onClick = useCallback<
    React.MouseEventHandler<HTMLElement | SVGElement>
  >(
    e => {
      const target = e.target as HTMLElement
      if (target.matches('.variable')) {
        e.stopPropagation()
        const id = target.getAttribute('data-path')
        const type = target.getAttribute('data-type')
        if (id) {
          if (type === 'variable') {
            openVariableForm(id)
          } else {
            setEditPlaceholder(id)
          }
        }
      } else if (asDropdown && !props.editableDropdown && !readOnly) {
        lastRange.current = getCurrentRange()
        viaKeyboard.current = false
        setShowContext(true)
      }
    },
    [asDropdown, openVariableForm, props.editableDropdown, readOnly]
  )

  const checkPattern = useCallback<React.KeyboardEventHandler<HTMLDivElement>>(
    e => {
      if (pattern) {
        // incantation based on library/text-editor.tsx which works
        const charStr = String.fromCharCode(e.charCode)
        // @ts-ignore - hmm this works, despite type warning, seems types are off here
        const attemptedValue = `${e.target.textContent}${charStr}`

        if (!pattern.test(attemptedValue)) {
          e.preventDefault()
        }
      }
    },
    [pattern]
  )

  // content editable can insert <br> which appears empty, try to ignore these
  const hasValue = localValue === '<br>' ? false : !!localValue

  return (
    <>
      <TextEditorWrapper
        readOnly={readOnly}
        className={`${compact ? 'compact' : ''} ${props.className ?? ''}`}
        ref={wrapperRef}
      >
        {icon ? (
          <IconWrap hasValue={hasValue} onClick={onClick}>
            {icon}
          </IconWrap>
        ) : null}
        <EditorWrap>
          <Wrapper className={hasValue || focused ? 'labelUp' : ''}>
            {compact ? null : (
              <Label>
                {hasValue || focused || readOnly
                  ? upLabel
                    ? upLabel
                    : label
                  : label}
              </Label>
            )}
            <StyledTextEditor>
              <ContentEditable
                innerRef={ref}
                html={localValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onKeyUpCapture={handleKeyUp}
                className="editable"
                disabled={readOnly || (asDropdown && !props.editableDropdown)}
                onPaste={pasteAsText}
                onClick={onClick}
                onKeyPress={pattern ? checkPattern : undefined}
              />
            </StyledTextEditor>
            {!readOnly &&
              (asDropdown ? (
                <RightIconWrapper>
                  <SVGChevronDown
                    onClick={() => {
                      if (!readOnly) {
                        lastRange.current = getCurrentRange()
                        viaKeyboard.current = false
                        setShowContext(true)
                      }
                    }}
                  />
                </RightIconWrapper>
              ) : (
                <AddVariableWrapper>
                  <Tooltip
                    content="Insert context placeholder"
                    position="left"
                    disabled={showContext}
                  >
                    <ButtonIcon
                      appearance="transparent"
                      type="button"
                      onClick={() => {
                        lastRange.current = getCurrentRange()
                        viaKeyboard.current = false
                        setShowContext(true)
                      }}
                      disabled={variableButtonDisabled}
                    >
                      <SVGAddVariable />
                    </ButtonIcon>
                  </Tooltip>
                </AddVariableWrapper>
              ))}
          </Wrapper>
        </EditorWrap>
      </TextEditorWrapper>
      {showContext && environment
        ? createPortal(
            <ClickAwayListener onClickAway={() => setShowContext(false)}>
              <StyledDropdownContents
                minWidth={450}
                className="dropdown-contents"
                dropdownPosition={dropdownPosition}
                maxWidth={dropdownPosition.originalWidth}
                ref={dropdownRef}
              >
                <ContextPicker
                  stepId={stepId}
                  onSelect={addContextMapping}
                  environmentId={environment.id}
                  onClose={() => setShowContext(false)}
                />
              </StyledDropdownContents>
            </ClickAwayListener>,
            document.body
          )
        : null}
      {editPlaceholder && contextMapping[editPlaceholder] ? (
        <PlaceholderModal
          mappingId={editPlaceholder}
          handleClose={() => setEditPlaceholder(undefined)}
          contextMapping={contextMapping[editPlaceholder]}
          readOnly={readOnly}
          {...(mode.mode === 'step'
            ? {stepId: mode.stepId}
            : {templateId: mode.templateId})}
        />
      ) : null}
    </>
  )
}
